/**
 * Where the team people picker finds people (#6602). Coworkers, the leader's colleagues and Network
 * stand alone; the four Marketplace sources sit under one heading, because they are one place with
 * four ways in.
 */
export enum TeamPeopleSourceId {
  Coworkers = "coworkers",
  LeaderColleagues = "leaderColleagues",
  Network = "network",
  MarketplaceSearch = "marketplaceSearch",
  BookmarkedIndividuals = "bookmarkedIndividuals",
  BookmarkedTeams = "bookmarkedTeams",
  BookmarkedCompanies = "bookmarkedCompanies",
}

export const MARKETPLACE_SOURCE_HEADING = "Marketplace";

export type TeamPeopleSource = {
  id: TeamPeopleSourceId;
  label: string;
  /** The heading the source sits under, if any. */
  heading?: typeof MARKETPLACE_SOURCE_HEADING;
  /** Why this viewer cannot use the source. A source with a reason is drawn disabled, never hidden. */
  unavailableReason?: string;
};

export const NO_MARKETPLACE_SEARCH_REASON = "Needs Marketplace Search access";
export const NO_MARKETPLACE_DIRECTORY_REASON = "Needs access to Marketplace profiles";

/** What the leader's firm is called when the picker knows no name for it. */
export const UNNAMED_LEADER_FIRM = "The leader's firm";

const SOURCE_LABELS: Record<TeamPeopleSourceId, string> = {
  [TeamPeopleSourceId.Coworkers]: "Coworkers",
  [TeamPeopleSourceId.LeaderColleagues]: "Leader's colleagues",
  [TeamPeopleSourceId.Network]: "Network",
  [TeamPeopleSourceId.MarketplaceSearch]: "Search",
  [TeamPeopleSourceId.BookmarkedIndividuals]: "Bookmarked individuals",
  [TeamPeopleSourceId.BookmarkedTeams]: "Bookmarked teams",
  [TeamPeopleSourceId.BookmarkedCompanies]: "Bookmarked companies",
};

/** The sources whose people are read with `GET marketplace/companies/{id}/individuals`. */
const READ_THROUGH_DIRECTORY = new Set<TeamPeopleSourceId>([
  TeamPeopleSourceId.BookmarkedCompanies,
  TeamPeopleSourceId.LeaderColleagues,
]);

const MARKETPLACE_SOURCES = new Set<TeamPeopleSourceId>([
  TeamPeopleSourceId.MarketplaceSearch,
  TeamPeopleSourceId.BookmarkedIndividuals,
  TeamPeopleSourceId.BookmarkedTeams,
  TeamPeopleSourceId.BookmarkedCompanies,
]);

/** The leader's colleagues, named after the leader's firm: "Smith & Lee colleagues" (#6860). */
export function leaderColleaguesLabel(firmName: string | null | undefined): string {
  const name = firmName?.trim();
  return name ? `${name} colleagues` : SOURCE_LABELS[TeamPeopleSourceId.LeaderColleagues];
}

/** What the results header calls the source, where there is room for more than the list's label. */
export function teamPeopleSourceTitle(id: TeamPeopleSourceId, leaderFirmName?: string | null): string {
  if (id === TeamPeopleSourceId.MarketplaceSearch) return "Search the Marketplace";
  if (id === TeamPeopleSourceId.Network) return "Your network";
  if (id === TeamPeopleSourceId.LeaderColleagues) return leaderColleaguesLabel(leaderFirmName);
  return SOURCE_LABELS[id];
}

/** The firm of the vendor team's leader, whose people a client staffing that team is offered (#6860). */
export type TeamLeaderFirm = {
  legalEntityId: string;
  /** The firm's name, when the picker knows it. */
  name?: string | null;
};

export type TeamPeopleSourceAccess = {
  /** Whether the viewer may search the marketplace: finding people they have not met yet. */
  canSearchMarketplace: boolean;
  /**
   * Whether the viewer may read marketplace profiles, which is what listing a company's people asks
   * of the API (`GET marketplace/companies/{id}/individuals`).
   */
  canReadMarketplaceDirectory: boolean;
  /**
   * Whether the viewer's own coworkers are people this team may take. Not when a client staffs the
   * vendor team: the client's own staff do not work for the firm it is hiring (#6859).
   */
  offersCoworkers: boolean;
  /**
   * The vendor leader's firm, when a client staffs a vendor team that has a leader (#6860). Its people
   * are read through the directory, as a bookmarked company's are. Without one there is no such source.
   */
  leaderFirm?: TeamLeaderFirm;
};

/**
 * Why this viewer cannot use the source, if they cannot. Search is discovery and needs the search
 * permission. A bookmark is somebody the viewer already saved, and the bookmark endpoints ask for no
 * marketplace permission, so bookmarked individuals and teams are open to everyone; a bookmarked
 * company's people, and the leader's colleagues, are read through the directory, so those two sources
 * need directory access.
 */
function unavailableReasonFor(id: TeamPeopleSourceId, access: TeamPeopleSourceAccess): string | undefined {
  if (id === TeamPeopleSourceId.MarketplaceSearch && !access.canSearchMarketplace) return NO_MARKETPLACE_SEARCH_REASON;
  if (!READ_THROUGH_DIRECTORY.has(id) || access.canReadMarketplaceDirectory) return undefined;
  return NO_MARKETPLACE_DIRECTORY_REASON;
}

/** Whether the source is one this team could take people from at all, whatever the viewer may read. */
function isOffered(id: TeamPeopleSourceId, access: TeamPeopleSourceAccess): boolean {
  if (id === TeamPeopleSourceId.Coworkers) return access.offersCoworkers;
  if (id === TeamPeopleSourceId.LeaderColleagues) return Boolean(access.leaderFirm);
  return true;
}

/**
 * Every source, each with the reason this viewer cannot use it, if there is one. A source is shown
 * disabled with its reason rather than left out, so nobody wonders where the Marketplace went.
 *
 * Coworkers and the leader's colleagues are the exceptions, and are left out rather than disabled.
 * For a client staffing the vendor team, Coworkers is not a source this team could ever use (#6859).
 * The leader's colleagues exist only once the vendor team has a leader whose firm they are (#6860).
 */
export function teamPeopleSources(access: TeamPeopleSourceAccess): TeamPeopleSource[] {
  const ids = Object.values(TeamPeopleSourceId).filter((id) => isOffered(id, access));
  return ids.map((id) => ({
    id,
    label: id === TeamPeopleSourceId.LeaderColleagues ? leaderColleaguesLabel(access.leaderFirm?.name) : SOURCE_LABELS[id],
    heading: MARKETPLACE_SOURCES.has(id) ? MARKETPLACE_SOURCE_HEADING : undefined,
    unavailableReason: unavailableReasonFor(id, access),
  }));
}

/**
 * The source the picker opens on: the one asked for when the viewer can use it, else the one they
 * used last, else the first of Coworkers, the leader's colleagues and the Network that the viewer can
 * use. That is the order a vendor team is staffed in: the leader's own firm, then the people the
 * staffer already knows (#6859, #6860).
 */
export function initialTeamPeopleSource(
  sources: readonly TeamPeopleSource[],
  requested?: TeamPeopleSourceId,
  lastUsed?: string | null,
): TeamPeopleSourceId {
  const usable = sources.filter((source) => !source.unavailableReason);
  const pick = (id: string | null | undefined) => usable.find((source) => source.id === id)?.id;
  // The Network needs no permission and is always offered, so a picker with nothing else to go on
  // ends there.
  return (
    pick(requested) ??
    pick(lastUsed) ??
    pick(TeamPeopleSourceId.Coworkers) ??
    pick(TeamPeopleSourceId.LeaderColleagues) ??
    TeamPeopleSourceId.Network
  );
}
